import React, { useMemo } from "react";
import styles from "./style.module.scss";
import { useSelector } from "react-redux";
import { RootState } from "../../../../state";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import annotationPlugin from "chartjs-plugin-annotation";
import { Line } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  annotationPlugin,
);

interface VCarPlotProps {
  previewCornerConfig?: Record<string, string | number>;
}

function VCarPlot({ previewCornerConfig }: VCarPlotProps) {
  const configLapData = useSelector(
    (state: RootState) => state.virtualLoops.configLapData,
  );

  const cornerConfig = useSelector(
    (state: RootState) => state.virtualLoops.cornerConfig,
  );

  const activeCornerConfig = previewCornerConfig || cornerConfig;

  const chartData = useMemo(() => {
    if (
      !configLapData ||
      !("vCar" in configLapData) ||
      !configLapData.vCar ||
      configLapData.vCar.length === 0
    ) {
      return {
        labels: [],
        datasets: [],
      };
    }

    const labels = configLapData.vCar.map((_, index) => index.toString());
    const data = configLapData.vCar;

    return {
      labels,
      datasets: [
        {
          label: "vCar Speed",
          data,
          pointRadius: 0,
          borderColor: "#A18B6B",
          borderWidth: 2,
        },
      ],
    };
  }, [configLapData]);

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: { duration: 0 },
    plugins: {
      tooltip: {
        enabled: false,
      },
      datalabels: {
        display: false,
      },
      legend: {
        display: false,
      },
      title: {
        display: true,
        color: "#ffffff",
        text: "Corner Positions on vCar",
      },
      annotation: {
        annotations:
          activeCornerConfig &&
          configLapData &&
          "sLap" in configLapData &&
          configLapData.sLap
            ? Object.entries(activeCornerConfig).reduce(
                (acc, [cornerName, sLapValue]) => {
                  if (sLapValue === "EOL") {
                    return acc;
                  }
                  const index = configLapData.sLap.findIndex(
                    (value) => value >= Number(sLapValue),
                  );

                  if (index !== -1) {
                    acc[cornerName] = {
                      type: "line" as const,
                      xMin: index,
                      xMax: index,
                      borderColor: "#ffffff",
                      borderWidth: 2,
                      label: {
                        display: true,
                        content: cornerName,
                        position: "start" as const,
                        color: "#000000",
                        backgroundColor: "#ffffff",
                        rotation: 0,
                        font: {
                          size: 8,
                        },
                      },
                    };
                  }
                  return acc;
                },
                {} as Record<string, object>,
              )
            : {},
      },
    },
    scales: {
      x: {
        border: {
          color: "#ffffff",
        },
        grid: {
          display: false,
        },
        ticks: {
          display: false,
        },
      },
      y: {
        grid: {
          color: "#505050",
        },
        ticks: {
          color: "#ffffff",
        },
        border: {
          color: "#ffffff",
        },
        title: {
          display: true,
          text: "vCar (km/h)",
          color: "#ffffff",
        },
      },
    },
  };

  return (
    <div className={styles.container}>
      <Line data={chartData} options={chartOptions} />
    </div>
  );
}

export default VCarPlot;
